import CloseIcon from "@mui/icons-material/Close";
import { Button, FormHelperText, Grid, TextField, Typography } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { FieldArray } from "formik";
import React from "react";

function EditKPITable({
  kpiActivities,
  values,
  setFieldValue,
  setValues,
  touched,
  errors,
  isMobileDisplay,
}) {
  const kpiFilter = createFilterOptions();

  const hasOneOrMoreDailyPointLimitChecked = values.kpiActivities.some(
    (kpiActivity) => kpiActivity.checked
  );

  return (
    <>
      <Grid container spacing={hasOneOrMoreDailyPointLimitChecked ? 2 : 4} mb="1rem">
        <Grid item xs={hasOneOrMoreDailyPointLimitChecked ? 3 : 5}>
          <Typography
            sx={{ lineHeight: "1.2rem", fontSize: "0.9rem", fontWeight: "600", color: "#344767" }}
          >
            Select KPIs
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography
            sx={{
              textAlign: isMobileDisplay ? "left" : "center",
              lineHeight: "1.2rem",
              fontSize: "0.9rem",
              fontWeight: "600",
              color: "#344767",
            }}
          >
            {isMobileDisplay ? "KPIs" : "KPIs Completed"}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography
            sx={{
              textAlign: isMobileDisplay ? "left" : "center",
              lineHeight: "1.2rem",
              fontSize: "0.9rem",
              fontWeight: "600",
              color: "#344767",
            }}
          >
            {isMobileDisplay ? "Points" : "Points Achieved"}
          </Typography>
        </Grid>
        <Grid item xs={2} container>
          <Typography
            sx={{
              textAlign: isMobileDisplay ? "left" : "center",
              lineHeight: "1.2rem",
              fontSize: "0.9rem",
              fontWeight: "600",
              color: "#344767",
            }}
          >
            {isMobileDisplay ? "Max Points" : "Daily Point Limit"}
          </Typography>
        </Grid>
      </Grid>
      <FieldArray name="kpiActivities">
        {({ push, remove }) =>
          values.kpiActivities.map((kpiActivity, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={`kpi-${i}`}>
              <Grid
                container
                spacing={hasOneOrMoreDailyPointLimitChecked ? 2 : 4}
                mb=".5rem"
                alignItems="flex-end"
              >
                <Grid item xs={hasOneOrMoreDailyPointLimitChecked ? 3 : 5}>
                  <Autocomplete
                    disableClearable
                    value={kpiActivity}
                    filterOptions={(options, params) => {
                      const filtered = kpiFilter(
                        kpiActivities.filter(
                          (kpi) => !values.kpiActivities.find((act) => act.name === kpi.name)
                        ),
                        params
                      );
                      return filtered;
                    }}
                    onBlur={(blurEvent) => {
                      if (!blurEvent?.target?.value?.trim()) return;
                      const foundKpi = kpiActivities.find(
                        (kpi) => kpi?.name === blurEvent?.target?.value
                      );
                      if (foundKpi) {
                        setFieldValue(`kpiActivities[${i}]`, {
                          id: foundKpi?.id || null,
                          name: foundKpi?.inputValue || foundKpi?.name || "",
                          count: values?.kpiActivities?.[i].count,
                          points: values?.kpiActivities?.[i].points,
                        });
                      } else if (kpiActivity.id) {
                        setFieldValue(`kpiActivities[${i}]`, {
                          id: kpiActivity.id,
                          name: kpiActivity.name,
                          count: kpiActivity.count,
                          points: kpiActivity.points,
                        });
                      } else {
                        setFieldValue(`kpiActivities[${i}]`, {
                          id: null,
                          name: blurEvent.target.value || "",
                          count: values?.kpiActivities?.[i].count,
                          points: values?.kpiActivities?.[i].points,
                        });
                      }
                    }}
                    options={kpiActivities.sort((a, b) => -b.name.localeCompare(a.name))}
                    getOptionLabel={(option) => {
                      if (option?.name) {
                        return option?.name;
                      }
                      return "";
                    }}
                    renderOption={(props, option) => (
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      <li {...props}>{option?.name || ""}</li>
                    )}
                    freeSolo
                    renderInput={(params) => (
                      <TextField
                        variant="standard"
                        error={
                          !!errors?.kpiActivities?.[i]?.name && touched?.kpiActivities?.[i]?.name
                        }
                        helperText={
                          touched?.kpiActivities?.[i]?.name && errors?.kpiActivities?.[i]?.name
                        }
                        {...params}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    type="number"
                    variant="standard"
                    helperText={errors?.kpiActivities?.[i]?.count}
                    value={kpiActivity.count}
                    onChange={(e) => {
                      const arr = values.kpiActivities;
                      arr[i].count = e.target.value;
                      setValues((pre) => ({ ...pre, kpiActivities: arr }));
                    }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    type="number"
                    variant="standard"
                    helperText={errors?.kpiActivities?.[i]?.points}
                    value={kpiActivity.points}
                    onChange={(e) => {
                      const arr = values.kpiActivities;
                      arr[i].points = e.target.value;
                      setValues((pre) => ({ ...pre, kpiActivities: arr }));
                    }}
                  />
                </Grid>
                <Grid item xs={2} container justifyContent="center">
                  <TextField
                    type="number"
                    variant="standard"
                    value={kpiActivity.maxPointsPerDay || ""}
                    onChange={(e) => {
                      const arr = [...values.kpiActivities];
                      arr[i] = { ...arr[i], maxPointsPerDay: e.target.value };
                      setValues((pre) => ({ ...pre, kpiActivities: arr }));
                    }}
                  />
                </Grid>
                <Grid item xs={1} container alignItems="center">
                  {values.kpiActivities.length !== 1 && (
                    <CloseIcon sx={{ cursor: "pointer" }} color="error" onClick={() => remove(i)} />
                  )}
                </Grid>
              </Grid>
              {values.kpiActivities.length - 1 === i &&
                typeof errors.kpiActivities === "string" && (
                  <FormHelperText sx={{ margin: "1rem 0rem", color: "red" }}>
                    {errors.kpiActivities}
                  </FormHelperText>
                )}
              {values.kpiActivities.length - 1 === i && (
                <Button
                  onClick={() => push({ id: null, count: 1, name: "", points: 1, new: true })}
                  variant="text"
                  sx={{
                    fontSize: "1rem",
                    color: "cornflowerblue",
                    textTransform: "none",
                    p: 0,
                    "&:hover": {
                      color: "#80a3e1",
                    },
                  }}
                >
                  + Add New
                </Button>
              )}
            </React.Fragment>
          ))
        }
      </FieldArray>
    </>
  );
}

export default EditKPITable;
